/**
 * Mock Budget Storage
 * 
 * This module provides a simple localStorage-based storage solution for mock budget data
 * in demo mode. This ensures that changes made in the Edit Sales Budget screen are
 * reflected in the Sales Budget tab view.
 * 
 * In production, this will be replaced with actual API calls to the backend.
 */

import type { CategoryBudgetRow } from '@/types/budget.types';

const STORAGE_KEY_PREFIX = 'mock_sales_budget_';

export interface StoredSalesBudgetCategories {
  projectId: number;
  categories: CategoryBudgetRow[];
  lastUpdated: string;
}

/**
 * Save sales budget categories to localStorage
 */
export function saveMockSalesBudgetCategories(
  projectId: number,
  categories: CategoryBudgetRow[]
): void {
  const data: StoredSalesBudgetCategories = {
    projectId,
    categories,
    lastUpdated: new Date().toISOString(),
  };
  
  try {
    const key = `sales-budget-categories-${projectId}`;
    localStorage.setItem(key, JSON.stringify(categories));
    
    // Also save with the old key format for backward compatibility
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${projectId}`,
      JSON.stringify(data)
    );
    
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key, value: categories }
    }));
    
    console.log('[MockBudgetStorage] Saved sales budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error saving to localStorage:', error);
  }
}

/**
 * Load sales budget categories from localStorage
 */
export function loadMockSalesBudgetCategories(
  projectId: number
): CategoryBudgetRow[] | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${projectId}`);
    if (!stored) {
      return null;
    }
    
    const data: StoredSalesBudgetCategories = JSON.parse(stored);
    console.log('[MockBudgetStorage] Loaded sales budget categories for project', projectId);
    return data.categories;
  } catch (error) {
    console.error('[MockBudgetStorage] Error loading from localStorage:', error);
    return null;
  }
}

/**
 * Clear sales budget categories from localStorage
 */
export function clearMockSalesBudgetCategories(projectId: number): void {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${projectId}`);
    console.log('[MockBudgetStorage] Cleared sales budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing localStorage:', error);
  }
}

/**
 * Clear all mock budget data from localStorage
 */
export function clearAllMockBudgetData(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('[MockBudgetStorage] Cleared all mock budget data');
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing all data:', error);
  }
}
